import 'vtk.js/Sources/Rendering/Profiles/Geometry';
import "vtk.js/Sources/Rendering/Profiles/Glyph";
import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';
import readPolyDataArrayBuffer from 'itk/readPolyDataArrayBuffer';
import vtkITKPolyDataReader from 'vtk.js/Sources/IO/Misc/ITKPolyDataReader';
import * as vtkMath from 'vtk.js/Sources/Common/Core/Math';

import vtkWidgetManager from "vtk.js/Sources/Widgets/Core/WidgetManager";
import vtkCoordinate from "vtk.js/Sources/Rendering/Core/Coordinate";

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Union } from "fable-compiler/bin/fable-library/Types";

import { displayMapTypes, ignore, logDebug, isNullOrUndefined, DicomView, getTopLevelDUName } from './Common';
import { useNotes } from '../Notes/Notes';
import { useAnatomyScalarBar } from '../AnatomyScalarBar/AnatomyScalarBar';
import { useRawThresholdSlider } from '../RawThresholdSlider/RawThresholdSlider';
import { useRawMapSummary } from '../RawMapSummary/RawMapSummary';
import { useVigrSummary } from '../VigrSummary/VigrSummary';
import { useCamera } from '../Camera/Camera';
import { useCalipers } from '../Calipers/Calipers';
import { useCenterlineMeasurement } from '../CenterlineMeasurement/CenterlineMeasurement';
import { useScreenshot } from '../Screenshot/Screenshot';
import { useInteraction } from './Interaction';
import { SlicePlane } from '../SlicePlane/SlicePlane';
import customTrackballInteractor from "./CustomTrackballInteractor";
import { applyRawThreshold, removeDisplayedMeshes, updateSelectedMap } from '../Meshes/Meshes';
import { useAnatomyOrientation } from '../AnatomyOrientation/AnatomyOrientationWidget';
import { useStentPlanningLabels, convertUnifiedBranchIndexToMasterIndex } from "../StentPlanning/StentPlanning";
import { useStentPlanningCenterlines } from "../StentPlanningCenterlines/StentPlanningCenterlines";
import { Vector3 } from "./Vector3";
import { setRendererViewPlaneNormal } from "../Camera/AdjustCamera";
import { CenterlineBranchSelection } from "../CenterlineMeasurement/CenterlinePlaneShared";

vtkITKPolyDataReader.setReadPolyDataArrayBufferFromITK(readPolyDataArrayBuffer);

const isDebugging = false;

function roundToPrecision(num, precision) {
    const multiplier = Math.pow(10, precision);
    return (Math.round(num * multiplier)) / multiplier;
}

function log(msg) {
    logDebug("MeshVisualization", isDebugging, msg);
}

function calculateAzimuth(dop, viewUp) {
    if (Math.abs(dop[0]) < 0.001 && Math.abs(dop[1]) < 0.001) {
        return vtkMath.degreesFromRadians(Math.atan2(viewUp[0], viewUp[1]));
    } else {
        return vtkMath.degreesFromRadians(Math.atan2(dop[0], dop[1]));
    }
}
function calculateElevation(dop, viewUp) {
    const elevation = -vtkMath.degreesFromRadians(Math.atan2(dop[2], Math.hypot(dop[0], dop[1])));
    const isUpsideDown = vtkMath.dot(viewUp, [0, 0, 1]) <= 0; // Check if we are upside-down
    if (!isUpsideDown) {
        return elevation;
    } else {
        if (elevation > 0) { return 180 - elevation; }
        else { return (-180) - elevation; }
    }
}

function multiplyScalar3D(v, scalar) {
    return [v[0] * scalar, v[1] * scalar, v[2] * scalar];
}

function rotate(position, rotationMatrix) {
    const newPos = [0, 0, 0];
    vtkMath.multiplyMatrix(rotationMatrix, position, 3, 3, 3, 1, newPos);
    return newPos;
}

function rotateX(position, angle) {
    const rotationMatrix =
        [
            1, 0, 0,
            0, Math.cos(angle), -Math.sin(angle),
            0, Math.sin(angle), Math.cos(angle)
        ];
    return rotate(position, rotationMatrix);
}

function rotateY(position, angle) {
    const rotationMatrix =
        [
            Math.cos(angle), 0, Math.sin(angle),
            0, 1, 0,
            -Math.sin(angle), 0, Math.cos(angle)
        ];
    return rotate(position, rotationMatrix);
}

function rotateZ(position, angle) {
    const rotationMatrix =
        [
            Math.cos(angle), -Math.sin(angle), 0,
            Math.sin(angle), Math.cos(angle), 0,
            0, 0, 1
        ];
    return rotate(position, rotationMatrix);
}

function rotateAzimuthAroundUp(v, angleDelta) {
    const angle = vtkMath.radiansFromDegrees(angleDelta);
    return rodriguesRotation(v, [0, 0, 1], angle);
}

function rotateElevation(pos, angleDelta) {
    const angle = vtkMath.radiansFromDegrees(angleDelta);
    return rotateX(pos, angle);
}

function rodriguesRotation(v, k, angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const vcos = multiplyScalar3D(v, cos);
    const kv = vtkMath.cross(k, v, [0, 0, 0]);
    const kvsin = multiplyScalar3D(kv, sin);
    const kdotv = vtkMath.dot(k, v);
    const kkvcos = multiplyScalar3D(k, (1 - cos) * kdotv);
    return vtkMath.add(vtkMath.add(vcos, kvsin, [0, 0, 0]), kkvcos, [0, 0, 0]);
}
function rotateElevationAroundAxis(v, dop, viewUp, angleDelta) {
    const angle = vtkMath.radiansFromDegrees(angleDelta);
    let k = [0, 0, 0];
    if (!vtkMath.areEquals([dop[0], dop[1], Math.abs(dop[2])], [0, 0, 1], 0.0001)) {
        vtkMath.cross(dop, [0, 0, 1], k);
    } else {
        vtkMath.cross(dop, viewUp, k);
    }
    vtkMath.normalize(k);
    return rodriguesRotation(v, k, angle);
}

/**
 * @typedef {object} Angles
 * @property {number} azimuth
 * @property {number} elevation
 */

/**
 * Rotate the camera to the specified angles
 * @param camera
 * @param {Angles} angles
 */
function rotateCamera(camera, angles) {
    const dop = camera.getDirectionOfProjection();
    const pos = camera.getPosition();
    const fp = camera.getFocalPoint();
    const relativePosition = vtkMath.subtract(pos, fp, [0, 0, 0]);
    const initialViewUp = camera.getViewUp();

    const azimuthDelta = calculateAzimuth(dop, initialViewUp) - angles.azimuth;
    const azimuthRotatedPosition = rotateAzimuthAroundUp(relativePosition, azimuthDelta);
    const azimuthRotatedViewUp = rotateAzimuthAroundUp(initialViewUp, azimuthDelta);

    const elevationDelta = calculateElevation(dop, initialViewUp) - angles.elevation;
    const elevationRotatedPosition = rotateElevation(azimuthRotatedPosition, elevationDelta);
    const elevationRotatedViewUp = rotateElevation(azimuthRotatedViewUp, elevationDelta);

    const finalPosition = vtkMath.add(elevationRotatedPosition, fp, [0, 0, 0]);

    camera.setViewUp(elevationRotatedViewUp);
    camera.setPosition(finalPosition[0], finalPosition[1], finalPosition[2]);
}


/**
 * @typedef {object} MapValuePickedArgs
 * @property {string} meshViewportLocation
 * @property {number?} maybePickedValue
 */

/**
 * @callback onMapValuePicked
 * @param {MapValuePickedArgs} mapValuePickedArgs
 * @returns {void}
 */

/**
 * @callback clearPickedValues
 * @param {void}
 * @returns {void}
 */

/**
 * @typedef {object} PickedMapValueProps
 * @property {onMapValuePicked} onMapValuePicked
 * @property notesProps
 * @property {clearPickedValues} clearPickedValues
 */

/**
 * MeshVisualization React component.
 * @param currentStudyMeshes
 * @param previousStudyMeshes
 * @param slicePlaneProps
 * @param staticNotesProps
 * @param measurementNotesProps
 * @param cameraProps
 * @param centerlineMeasurementProps
 * @param calipersProps
 * @param screenshotProps
 * @param selectedMap
 * @param showCenterline
 * @param overlayPreviousStudyMaps
 * @param interactionModeProps
 * @param onViewportResized
 * @param [anatomyOrientationProps]
 * @param showPreviousStudyMapsOnly
 * @param {PickedMapValueProps} pickedMapValueProps
 * @param meshViewportLocation
 * @param isSliceScroll
 * @param onMeshSliceChange
 * @param isStentPlanning
 * @param showStentPlanningCenterlines
 * @param stentPlanningCenterlinesProps
 * @param stentPlanningProps
 * @param isVisible
 * @param onRotationChange
 * @param rotationAngles
 * @param eventNames
 * @param {Union} selectedDicomView
 * @param {function} props.onVisualizationReady - Callback to invoke when the visualization is ready.
 */
const MeshVisualization = (
    {
        viewportId,
        currentStudyMeshes,
        previousStudyMeshes,
        slicePlaneProps,
        staticNotesProps,
        measurementNotesProps,
        cameraProps,
        centerlineMeasurementProps,
        calipersProps,
        screenshotProps,
        selectedMap,
        showCenterline,
        overlayPreviousStudyMaps,
        interactionModeProps,
        onViewportResized,
        anatomyOrientationProps,
        showPreviousStudyMapsOnly = false,
        pickedMapValueProps,
        meshViewportLocation,
        isSliceScroll,
        onMeshSliceChange,
        isStentPlanning,
        showStentPlanningCenterlines,
        stentPlanningCenterlinesProps,
        stentPlanningProps,
        isVisible = true,
        onRotationChange,
        eventNames,
        selectedDicomView,
        onVisualizationReady,
        rawThreshold = 0,
        onRawThresholdChange
    }) => {

    const containerRef = useRef(null);
    const vtkContainerRef = useRef(null);
    const context = useRef(null);
    let ctx = null;

    // Internal state used to trigger a re-render for hooks that have a dependency on the parent context
    const [initialized, setInitialized] = useState(false);

    function _captureCustomScreenshot() {
        return new Promise((resolve, reject) => {
            if (!context.current?.genericRenderWindow) {
                console.error("Cannot capture screenshot: missing required contexts");
                reject();
                return;
            }

            try {
                context.current.renderWindow.render();
                const vtkCanvas = context.current.genericRenderWindow
                    .getApiSpecificRenderWindow()
                    .getCanvas();

                if (!vtkCanvas) {
                    console.error("VTK canvas not available");
                    reject();
                    return;
                }

                const width = vtkCanvas.width;
                const height = vtkCanvas.height;
                const dpr = window.devicePixelRatio || 1;

                const compositeCanvas = document.createElement('canvas');
                compositeCanvas.width = width / dpr;
                compositeCanvas.height = height / dpr;
                const compositeContext = compositeCanvas.getContext('2d');

                if (!compositeContext) {
                    console.error("Failed to create composite canvas context");
                    reject();
                    return;
                }

                // Draw VTK canvas (3D mesh) as the base layer.
                compositeContext.drawImage(vtkCanvas, 0, 0, compositeCanvas.width, compositeCanvas.height);

                // Get the Konva label layer canvas.
                // const labelLayerCanvas = context.current.layer?.getCanvas?.();

                // Draw Konva centerline canvas if available.
                let allCanvases;
                if (isStentPlanning) {
                    // TODO: this should be in StentPlanning.js, but this was easier
                    const stentContainer =
                        Array.from(vtkContainerRef.current?.parentNode.childNodes)
                            .find((node) => {
                                return node !== vtkContainerRef.current;
                            });
                    allCanvases = stentContainer?.querySelectorAll('canvas');
                } else {
                    allCanvases = vtkContainerRef.current?.querySelectorAll('canvas');
                }

                if (allCanvases) {
                    allCanvases.forEach(canvas => {
                        if (canvas !== vtkCanvas) {
                            compositeContext.drawImage(canvas, 0, 0, compositeCanvas.width, compositeCanvas.height);
                        }
                    });
                }

                const canvasInfo = {
                    viewportIndex: viewportId,
                    canvasData: compositeCanvas.toDataURL('image/png')
                };
                resolve(canvasInfo);
            } catch (error) {
                console.error("Error capturing screenshot:", error);
                reject();
            }
        });
    }

    function captureCustomScreenshot(ev) {
        if (ev.detail.viewportId !== viewportId) {
            return;
        }

        if (typeof ev.detail.callback !== 'function') {
            console.error("Invalid event detail for screenshot capture");
            return;
        }

        // Wait for a short delay to ensure all rendering is complete.
        setTimeout(async () => {
            try {
                const ret = await _captureCustomScreenshot();
                ev.detail.callback(ret);
            } catch (e) {
                console.error(e);
            }
        }, 100);
    }

    const _fullyResetCamera = () => {
        if (context.current && !isStentPlanning) {
            const camera = context.current.renderer.getActiveCamera();
            const bounds = currentStudyMeshes?.anatomy?.actor?.getBounds();
            resetCamera();
            bounds ? context.current.renderer.resetCamera(bounds) : context.current.renderer.resetCamera();
            context.current.renderer.resetCameraClippingRange();
            const dop = camera.getDirectionOfProjection();
            const viewUp = camera.getViewUp();
            onRotationChange({
                azimuth: calculateAzimuth(dop, viewUp),
                elevation: calculateElevation(dop, viewUp)
            });
        }
    }

    const _snapToAngle = (angleEvent) => {
        if (context.current && !isStentPlanning) {
            const camera = context.current.renderer.getActiveCamera();
            resetCamera();
            rotateCamera(camera, angleEvent);
            const dop = camera.getDirectionOfProjection();
            const viewUp = camera.getViewUp();
            onRotationChange({
                azimuth: calculateAzimuth(dop, viewUp),
                elevation: calculateElevation(dop, viewUp)
            });
        }
    };

    const _incrementAngle = (incrementDetail) => {
        if (context.current && !isStentPlanning) {
            const { renderWindow, renderer } = context.current;
            const camera = renderer.getActiveCamera();
            const { azimuth, elevation } = incrementDetail;
            const pos = camera.getPosition();
            const fp = camera.getFocalPoint();
            const relativePosition = vtkMath.subtract(pos, fp, [0, 0, 0]);
            const initialViewUp = camera.getViewUp();
            let dop = camera.getDirectionOfProjection();

            const azimuthRotatedPosition = rotateAzimuthAroundUp(relativePosition, azimuth);
            const azimuthRotatedDop = rotateAzimuthAroundUp(dop, azimuth);
            const azimuthRotatedViewUp = rotateAzimuthAroundUp(initialViewUp, azimuth);

            // Rounding errors can cause weird behaviours at certain angles, so we use fixed precision here
            const approxViewUp = [
                roundToPrecision(initialViewUp[0], 4),
                roundToPrecision(initialViewUp[1], 4),
                roundToPrecision(initialViewUp[2], 4)
            ];
            const sign = vtkMath.dot(approxViewUp, [0, 0, 1]) >= 0 ? 1 : -1;
            const adjustedElevation = elevation * sign;

            const elevationRotatedPosition = rotateElevationAroundAxis(azimuthRotatedPosition, azimuthRotatedDop, azimuthRotatedViewUp, adjustedElevation);
            const elevationRotatedViewUp = rotateElevationAroundAxis(azimuthRotatedViewUp, azimuthRotatedDop, azimuthRotatedViewUp, adjustedElevation);

            const finalPosition = vtkMath.add(elevationRotatedPosition, fp, [0, 0, 0]);

            camera.setViewUp(elevationRotatedViewUp);
            camera.setPosition(finalPosition[0], finalPosition[1], finalPosition[2]);
            renderer.resetCameraClippingRange();
            renderWindow.render();
            dop = camera.getDirectionOfProjection();
            const viewUp = camera.getViewUp();

            onRotationChange({
                azimuth: calculateAzimuth(dop, viewUp),
                elevation: calculateElevation(dop, viewUp)
            });
        }
    }

    const fullyResetCamera = (e) => { _fullyResetCamera(); };
    const snapToAngle = (e) => { _snapToAngle(e.detail); };
    const incrementAngle = (e) => { _incrementAngle(e.detail); };

    const onRotateFunc = update => {
        if (context.current?.renderer) {
            const camera = context.current.renderer.getActiveCamera();
            const dop = camera.getDirectionOfProjection();
            const viewUp = camera.getViewUp();
            const azimuth = calculateAzimuth(dop, viewUp);
            const elevation = calculateElevation(dop, viewUp);
            update({ azimuth: azimuth, elevation: elevation });
        }
    }

    function onRotate() {
        onRotateFunc(onRotationChange);
    }

    const setFocalPointByIndex = (e) => {
        if (context.current?.renderer) {
            const { index, branch } = e.detail;
            const branchType = branch.name === 'Left' ? CenterlineBranchSelection.TRANSITION_LEFT : CenterlineBranchSelection.TRANSITION_RIGHT;
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, branchType, currentStudyMeshes.wallCenterline.dataSource.getFieldData());
            const pointData = currentStudyMeshes.wallCenterline.dataSource.getPoints();
            const [x, y, z] = pointData.getTuple(masterIndex);
            const camera = context.current.renderer.getActiveCamera();
            camera.setFocalPoint(x, y, z);
        }
    };

    selectedMap = selectedMap.name ?? selectedMap;

    const render = () => {
        if (context.current && isVisible) {
            log(meshViewportLocation + " - Rendering");

            // OPTIMIZE:
            // This is a quick fix for the resolution issue when switching between single/quad view.
            // A better fix would be to depend on a visibility prop.
            context.current.genericRenderWindow.resize();
        }
    };

    render();

    //Set up resize observer for the container
    useEffect(() => {
        if (context.current) {
            const handleResize = () => {
                if (containerRef.current && onViewportResized) {
                    log(meshViewportLocation + " - Resizing");
                    context.current.genericRenderWindow.resize();
                    const dims = containerRef.current.getBoundingClientRect();
                    const bounds = { width: dims.width, height: dims.height };
                    onViewportResized(bounds);

                    if (context.current?.updatePickedIndicator) {
                        context.current.updatePickedIndicator();
                    }

                    context.current.renderWindow.render();
                }
            };

            if (!context.current.resizeObserver) {
                // Render on container resizes
                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(containerRef.current);
                context.current.resizeObserver = resizeObserver;
            }
        }
        ctx = context.current;
    }, [containerRef.current, isVisible, onViewportResized])

    useEffect(() => {
        if (!context.current && currentStudyMeshes) {
            log(meshViewportLocation + " - Reading mesh data");
            // We use the wrapper here to abstract out manual RenderWindow/Renderer/OpenGLRenderWindow setup
            const genericRenderWindow = vtkGenericRenderWindow.newInstance({
                background: isStentPlanning ? [255.0, 255.0, 255.0] : [0.0, 0.0, 0.0]
            });

            // TODO: Might be able to directly add this to the jsx in the return statement.
            const vtkContainer = document.createElement('div');
            vtkContainer.id = 'vtkContainer';
            vtkContainer.style.position = 'absolute';
            vtkContainer.style.top = '0';
            vtkContainer.style.left = '0';
            vtkContainer.style.width = '100%';
            vtkContainer.style.height = '100%';
            containerRef.current.appendChild(vtkContainer);
            vtkContainerRef.current = vtkContainer;

            genericRenderWindow.setContainer(vtkContainerRef.current);
            genericRenderWindow.resize();

            const renderer = genericRenderWindow.getRenderer();
            const renderWindow = genericRenderWindow.getRenderWindow();

            const istyle = customTrackballInteractor.newInstance();

            istyle.setMouseWheelIsSliceScroll(isSliceScroll);
            istyle.setIsStentPlanning(isStentPlanning);
            istyle.setUpdateSliceMethod(function (delta) {
                onMeshSliceChange(delta);
            });
            if (!isStentPlanning) {
                istyle.setOnMouseRotated(onRotate);
                window.addEventListener(eventNames.FullyResetCamera, fullyResetCamera);
                window.addEventListener(eventNames.SetAngle, snapToAngle);
                window.addEventListener(eventNames.IncrementAngle, incrementAngle);
                window.addEventListener(eventNames.SetFocalPointByIndex, setFocalPointByIndex);
            }
            if (eventNames?.CaptureCustomScreenshot) {
                window.addEventListener(eventNames.CaptureCustomScreenshot, captureCustomScreenshot);
            }
            istyle.onEndRotateEvent(() => { onRotateFunc(onRotationChange); });
            renderWindow.getInteractor().setInteractorStyle(istyle);

            const widgetManager = vtkWidgetManager.newInstance();
            widgetManager.setRenderer(renderer);

            // ----------------------------------------------------------------------------
            // Set up the data
            // ----------------------------------------------------------------------------

            context.current = {
                genericRenderWindow,
                renderWindow,
                renderer,
                widgetManager,
                istyle,
                pickMapValueSubscription: null,
                deletePickedIndicator: ignore,
                updatePickedIndicator: ignore,
                cameraModifiedSubscription: null,
                cameraProps
            };

            updateSelectedMap(
                meshViewportLocation,
                context.current,
                selectedMap,
                renderer,
                currentStudyMeshes,
                interactionModeProps.interactionMode,
                pickedMapValueProps,
                false,
                isStentPlanning,
                rawThreshold);

            renderer.resetCamera();
            // TIP: If the picked indicator isn't being updated in some cases, try also subscribing to renderWindow.onModified.
            context.current.cameraModifiedSubscription = renderer.getActiveCamera().onModified(() => {
                log(meshViewportLocation + " - renderer.getActiveCamera().onModified");
                if (context.current.updatePickedIndicator) {
                    context.current.updatePickedIndicator();
                    renderWindow.render();
                }
            });

            renderWindow.render();

            setInitialized(true);
            log(meshViewportLocation + " - Component has been initialized");
            onVisualizationReady?.([viewportId, true]);
        }

        ctx = context.current;
    }, [currentStudyMeshes]);

    // Side effect for updating visualization with selected map. useLayoutEffect (not useEffect)
    // so vtk re-renders synchronously with the layout change, avoiding a stale-content flash.
    useLayoutEffect(() => {
        if (context.current && currentStudyMeshes) {
            const { renderer, renderWindow } = context.current;

            if (previousStudyMeshes && showPreviousStudyMapsOnly === true) {
                log(`${meshViewportLocation} - Changing previous study selected map to ${selectedMap}`);
                removeDisplayedMeshes(renderer, currentStudyMeshes);
                updateSelectedMap(
                    meshViewportLocation,
                    context.current,
                    selectedMap,
                    renderer,
                    previousStudyMeshes,
                    interactionModeProps.interactionMode,
                    pickedMapValueProps,
                    false,
                    false,
                    rawThreshold);
            } else {
                log(`${meshViewportLocation} - Changing current study selected map to ${selectedMap}`);
                if (previousStudyMeshes) {
                    removeDisplayedMeshes(renderer, previousStudyMeshes);
                }
                updateSelectedMap(
                    meshViewportLocation,
                    context.current,
                    selectedMap,
                    renderer,
                    currentStudyMeshes,
                    interactionModeProps.interactionMode,
                    pickedMapValueProps,
                    false,
                    isStentPlanning,
                    rawThreshold);
            }
            log(meshViewportLocation + " - Rendering after selected map change");
            renderWindow.render();
        }
        ctx = context.current;
    }, [selectedMap, currentStudyMeshes, previousStudyMeshes, showPreviousStudyMapsOnly]);

    // Side effect for RAW threshold changes — a single persistent rAF loop chases the latest
    // target with exponential-decay smoothing so the brown plateau slides through the gradient
    // rather than snapping. The loop is mounted on viewport identity, NOT on rawThreshold, so
    // rapid slider input doesn't tear it down — slider updates flow in via the target ref.
    const rawAnimatedThresholdRef = useRef(rawThreshold);
    const rawTargetThresholdRef = useRef(rawThreshold);
    const rawRafIdRef = useRef(null);
    const rawWakeRef = useRef(null);
    rawTargetThresholdRef.current = rawThreshold;
    useEffect(() => {
        if (!context.current || !currentStudyMeshes) return;
        if (selectedMap !== displayMapTypes.CONTINUOUS_RAW) {
            rawAnimatedThresholdRef.current = rawTargetThresholdRef.current;
            return;
        }

        const meshes = (previousStudyMeshes && showPreviousStudyMapsOnly === true)
            ? previousStudyMeshes
            : currentStudyMeshes;
        const timeConstantMs = 25;
        const settleEpsilon = 1e-3;
        const motionBandWidth = 0.05;
        const restingBandWidth = 1e-5;
        let lastTime = performance.now();

        const step = (now) => {
            const dt = now - lastTime;
            lastTime = now;
            const target = rawTargetThresholdRef.current;
            const current = rawAnimatedThresholdRef.current;
            if (Math.abs(target - current) < settleEpsilon) {
                rawAnimatedThresholdRef.current = target;
                applyRawThreshold(meshes, target, restingBandWidth);
                context.current.renderWindow.render();
                rawRafIdRef.current = null;
                return;
            }
            const alpha = 1 - Math.exp(-dt / timeConstantMs);
            const next = current + (target - current) * alpha;
            rawAnimatedThresholdRef.current = next;
            applyRawThreshold(meshes, next, motionBandWidth);
            context.current.renderWindow.render();
            rawRafIdRef.current = requestAnimationFrame(step);
        };

        const wake = () => {
            if (rawRafIdRef.current !== null) return;
            lastTime = performance.now();
            rawRafIdRef.current = requestAnimationFrame(step);
        };
        rawWakeRef.current = wake;
        wake();

        return () => {
            if (rawRafIdRef.current !== null) cancelAnimationFrame(rawRafIdRef.current);
            rawRafIdRef.current = null;
            rawWakeRef.current = null;
        };
    }, [selectedMap, currentStudyMeshes, previousStudyMeshes, showPreviousStudyMapsOnly]);

    useEffect(() => {
        rawWakeRef.current?.();
    }, [rawThreshold]);

    // Side effect for previous study overlay
    useEffect(() => {
        if (previousStudyMeshes) {
            const { renderer, renderWindow, } = context.current;

            if (showPreviousStudyMapsOnly === false) {
                removeDisplayedMeshes(renderer, previousStudyMeshes);

                // Don't overlay when showing previous study
                if (overlayPreviousStudyMaps && showPreviousStudyMapsOnly === false) {
                    log(meshViewportLocation + " - Showing previous map overlay");
                    updateSelectedMap(
                        meshViewportLocation,
                        context.current,
                        selectedMap,
                        renderer,
                        previousStudyMeshes,
                        interactionModeProps.interactionMode,
                        pickedMapValueProps,
                        true,
                        false,
                        rawThreshold);
                }
                log(meshViewportLocation + " - Rendering after previous study overlay change");
                renderWindow.render();
            }
        }
        ctx = context.current;
    }, [selectedMap, overlayPreviousStudyMaps, previousStudyMeshes, showPreviousStudyMapsOnly]);

    useEffect(() => {
        if (context.current) {
            log(`${meshViewportLocation} - Setting isSliceScroll to ${isSliceScroll}`);
            context.current.istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        }
        ctx = context.current;
    }, [isSliceScroll]);

    useEffect(() => {
        if (context.current) {
            log(`${meshViewportLocation} - Setting isStentPlanning to ${isStentPlanning}`);
            context.current.istyle.setIsStentPlanning(isStentPlanning);
        }
        ctx = context.current;
    }, [isStentPlanning])

    // Side effect for updating the interactor's slice changed callback.
    useEffect(() => {
        if (context.current) {
            context.current.istyle.setUpdateSliceMethod(function (delta) {
                onMeshSliceChange(delta);
            });
        }
    }, [onMeshSliceChange])

    function resetCamera() {
        if (context.current && cameraProps.initialCameraAdjustment) {
            let adjustment = Vector3.ofVtk(cameraProps.initialCameraAdjustment);
            setRendererViewPlaneNormal(context.current.renderer, adjustment);
        }
    }

    // Camera side effect
    useCamera(vtkContainerRef.current, context.current, initialized, cameraProps);

    // Notes side effect
    const [redrawMeasurementNotes, ...notesInteractionMeasurement] = useNotes("measurement", vtkContainerRef.current,
        context.current,
        measurementNotesProps);
    const [redrawStaticNotes, ...notesInteractionStatic] = useNotes("static", vtkContainerRef.current,
        context.current,
        staticNotesProps);
    const [redrawPickedMapValueNotes, ...notesInteractionPickedMapValue] = useNotes("pickedMapValue", vtkContainerRef.current,
        context.current,
        pickedMapValueProps.notesProps);


    // Anatomy Scalar bar side effect
    const showAnatomyScalarBar = (selectedMap === displayMapTypes.ANATOMY && !isStentPlanning);
    const [redrawAnatomyScalarBar] = useAnatomyScalarBar(vtkContainerRef, initialized, showAnatomyScalarBar);

    // In compare view the previous-study viewport reads from previousStudyMeshes.
    const activeMeshes = (previousStudyMeshes && showPreviousStudyMapsOnly === true)
        ? previousStudyMeshes
        : currentStudyMeshes;

    const showRawThresholdSlider = selectedMap === displayMapTypes.CONTINUOUS_RAW;
    useRawThresholdSlider(vtkContainerRef, initialized, showRawThresholdSlider, rawThreshold, onRawThresholdChange, activeMeshes?.scalarBarActor);

    const showRawMapSummary = selectedMap === displayMapTypes.CONTINUOUS_RAW;
    useRawMapSummary(vtkContainerRef, initialized, showRawMapSummary, activeMeshes, rawThreshold);

    const showVigrSummary = selectedMap === displayMapTypes.VIGR;
    useVigrSummary(vtkContainerRef, initialized, showVigrSummary, activeMeshes);

    const redrawCanvases = (canvas, dims) => {
        redrawMeasurementNotes(canvas, dims);
        redrawStaticNotes(canvas, dims);
        redrawAnatomyScalarBar(canvas, dims);
        redrawPickedMapValueNotes(canvas, dims);
    };

    if (isStentPlanning === false) {
        // Centerline Measurement side effect
        useCenterlineMeasurement(containerRef.current, context.current, centerlineMeasurementProps);
    }

    if (isStentPlanning === true && !isNullOrUndefined(stentPlanningCenterlinesProps)) {
        const konvaStage = useStentPlanningLabels(containerRef.current, context.current, currentStudyMeshes, stentPlanningProps);
        useStentPlanningCenterlines(containerRef.current, context.current, konvaStage, currentStudyMeshes, stentPlanningCenterlinesProps);
    } else if (!isNullOrUndefined(stentPlanningCenterlinesProps)) {
        useStentPlanningCenterlines(vtkContainerRef.current, context.current, null, currentStudyMeshes, stentPlanningCenterlinesProps);
    }

    // Calipers side effect
    useCalipers(context.current, initialized, calipersProps, currentStudyMeshes);

    // Screenshot side effect
    const [captureScreenshot, ...screenshotInteraction] = useScreenshot(vtkContainerRef.current, context.current, redrawCanvases, screenshotProps);

    // Interaction side effect
    useInteraction(
        context.current,
        interactionModeProps,
        notesInteractionStatic,
        notesInteractionMeasurement,
        screenshotInteraction,
        captureScreenshot,
        pickedMapValueProps,
        currentStudyMeshes,
        previousStudyMeshes,
        showPreviousStudyMapsOnly,
        meshViewportLocation,
        selectedMap);

    // Orientation marker side effect
    if (!isNullOrUndefined(anatomyOrientationProps))
        useAnatomyOrientation(context.current, initialized, anatomyOrientationProps);

    useEffect(() => {
        if (context.current && currentStudyMeshes) {
            log(meshViewportLocation + " - Toggling centerline");

            const { renderer, renderWindow } = context.current;

            if (showCenterline) {
                renderer.addActor(currentStudyMeshes.lumenCenterline.actor);
            } else {
                renderer.removeActor(currentStudyMeshes.lumenCenterline.actor);
            }
            log(meshViewportLocation + " - Rendering after centerline toggle change");
            renderWindow.render();
        }
        ctx = context.current;
    }, [showCenterline]);

    useEffect(() => {
        log(meshViewportLocation + " - Mounting");
        onVisualizationReady?.([viewportId, false]);
        return () => {
            if (context.current) {
                log(meshViewportLocation + " - Destroying component");
                const {
                    genericRenderWindow,
                    resizeObserver,
                    widgetManager,
                    pickMapValueSubscription,
                    cameraModifiedSubscription
                } = context.current;

                if (pickMapValueSubscription) {
                    pickMapValueSubscription.unsubscribe();
                }

                if (cameraModifiedSubscription) {
                    cameraModifiedSubscription.unsubscribe();
                }

                if (!isStentPlanning) {
                    window.removeEventListener(eventNames.FullyResetCamera, fullyResetCamera);
                    window.removeEventListener(eventNames.SetAngle, snapToAngle);
                    window.removeEventListener(eventNames.IncrementAngle, incrementAngle);
                    window.removeEventListener(eventNames.SetFocalPointByIndex, setFocalPointByIndex);
                }
                if (eventNames?.CaptureCustomScreenshot) {
                    window.removeEventListener(eventNames.CaptureCustomScreenshot, captureCustomScreenshot);
                }

                // TODO VRM-1536 - Do we need to dispose here? Who should be responsible for currentStudyMeshes's lifecycle?
                // This also causes a crash if the DOM location of the vtk.js visualization ever changes
                //if (currentStudyMeshes) {
                //    currentStudyMeshes.disposeMeshes(currentStudyMeshes);
                //}
                genericRenderWindow.delete();
                resizeObserver.disconnect();
                widgetManager.delete();

                context.current = null;
            }
        };
    }, []);

    // Effect to update the interactor scroll factor based on the the selected DICOM view
    useEffect(() => {
        if (context.current?.istyle) {
            const { istyle } = context.current;
            if (selectedDicomView.name === DicomView.CENTERLINE) {
                istyle.setScrollFactor(1);
            } else {
                istyle.setScrollFactor(2);
            }
        }
    }, [selectedDicomView]);

    return (
        <div className="layout-view-view-container" ref={containerRef}>
            {slicePlaneProps.map((props) => (
                <SlicePlane
                    key={`${meshViewportLocation}-slicePlane-${getTopLevelDUName(props.dicomViewMode)}`}
                    props={{
                        genericRenderWindow: context.current?.genericRenderWindow,
                        isStentPlanning,
                        slicePlaneProps: props
                    }} />
            ))}
        </div>
    );
};

export default MeshVisualization;